import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, QueryList, ViewChildren } from '@angular/core';
import { Filter } from '@idto';
import { OverlayService } from '@service/overlay/overlay.service';
import { ProductsService } from '@service/products/products.service';
import { StorageService } from '@service/storage/storage.service';
import { WindowRefService } from '@service/window-service/window-ref.service';
import { filter, takeUntil } from 'rxjs/operators';
import { StringVariable } from 'app/model/StringVariable.model'
import { ParsedSearchResult } from 'app/model/ParsedSearchResult.model';
import { Resolutions } from '@resolutions';
import { Subject } from 'rxjs';
import { AnalyticsService } from '@service/analytics/abstract/analytics.service';

@Component({
    selector: 'app-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FiltersComponent implements OnDestroy 
{
  //#region Backfields
  private _filters: Filter[];
  private _searchResult: ParsedSearchResult;
  //#endregion

  private destroy$: Subject<void> = new Subject();

  get filters(): Filter[]
  {
    return this._filters;
  }
  get searchResult(): ParsedSearchResult
  {
    return this._searchResult;
  }

  @Input() set searchResult(value: ParsedSearchResult)
  {
    this._searchResult = value;
    this._filters = value.filters;
    if (this._filters.length == 0)
    {
      // @ts-ignore
      this._filters = [this.createEmptySelectFilter(6), this.createEmptySelectFilter(4), this.createEmptySelectFilter(6), this.createEmptySelectFilter()];
      this.changeDetection.detectChanges();
      return;
    }

    this._filters.forEach((filter: Filter) =>
    {
      filter['isInterval'] = filter.type === StringVariable.INTERVAL;

      if (filter.values && filter.values.length)
      {
        // set filter as visible if there are 1 or more elements
        filter['visible'] = filter.values.filter(fV => fV.recordCount >= 1).length || filter.values.filter(el => el.selected).length;
      }
      if (filter.type === StringVariable.INTERVAL || filter.code === StringVariable.CATEGORY_CODE)
      {
        filter['visible'] = true;
      }
    });

    this.storageService.get('openFilters').pipe(filter(v => !!v)).subscribe(res =>
    {
      for (let i = 0; i < this._filters.length; i++)
      {
        if (res[i] && this._filters[i].code == res[i].code)
        {
          this._filters[i]['defaultExpanded'] = res[i].defaultExpanded;
        }
      }
    });

    this._filters.forEach((filter: Filter) =>
    {
      if ((filter.type === "Select" || filter.type === "Multiselect") && filter.values.some(value => value?.selected))
      {
        filter.defaultExpanded = true;
      }
    });

    setTimeout(() =>
    {
      let filters = this.filterElems.filter((e) => e.nativeElement.id === this.idToGoBackTo);

      if (filters.length > 0)
      {
        let filter = filters[0];
        let currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        let currentTop = filter.nativeElement.getBoundingClientRect().top;

        if (!this.isInViewport(filter.nativeElement.getBoundingClientRect()))
        {
          window.scrollTo(0, currentScrollPosition + currentTop - this.topPositionOfIdToGoBackTo);
          this.topPositionOfIdToGoBackTo = 0;
          this.idToGoBackTo = null;
        }
      }
    }, 0);
  }

  @ViewChildren('filterElems') filterElems: QueryList<ElementRef>;

  public show: boolean = false;
  public activeFilters;
  @Output() public readonly valueChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() public readonly changeHeight: EventEmitter<void> = new EventEmitter<void>();
  @Output() public readonly hideBar: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public readonly showTooltipPopup: EventEmitter<any> = new EventEmitter<any>();
  @Output() public readonly activeFilterRemoved: EventEmitter<any> = new EventEmitter<any>();

  private idToGoBackTo: string;
  private topPositionOfIdToGoBackTo: number;

  public removeActiveFilter(filter: Filter): void
  {
    this.activeFilterRemoved.emit(filter);
  }

  constructor(private changeDetection: ChangeDetectorRef,
    private storageService: StorageService,
    private windowRef: WindowRefService,
    private productsService: ProductsService,
    private overlayService: OverlayService,
    private analyticsService: AnalyticsService)
  {
    productsService.activeFilters$.pipe(takeUntil(this.destroy$)).subscribe(res =>
    {
      this.activeFilters = res;
    });
  }

  public getFilterPosition(id: string): number
  {
    let filters = this.filterElems.filter((e) => e.nativeElement.id === id);

    if (filters.length > 0)
    {
      return filters[0].nativeElement.getBoundingClientRect().top;
    }

    return 0;
  }

  public isInViewport(rect: DOMRect)
  {
    return rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
  }

  ngOnDestroy(): void
  {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public showFilterBar(): void
  {
    this.show = true;
    this.windowRef.blockScroll();
    this.changeDetection.detectChanges();
  }

  public closeFilterBar(): void
  {
    const wrapper = document.getElementsByClassName('filter-wrapper');
    if (wrapper)
    {
      if (wrapper[0].classList.contains('show'))
      {
        this.show = false;
        this.overlayService.hide();
        this.windowRef.unBlockScroll();
        this.hideBar.emit(false);
        this.changeDetection.detectChanges();
      }
    }
  }

  @HostListener("window:resize", ["$event"])
  private onResize(): void
  {
    if (this.show && this.windowRef.getNativeWindowInnerWidth() >= Resolutions.resolutionDesktop)
    {
      this.closeFilterBar();
    }
  }

  public changeViewOfFilter(filterLocal: Filter): void
  {
    filterLocal.defaultExpanded = !filterLocal.defaultExpanded;

    if (filterLocal.defaultExpanded)
    {
      filter['changeExpand'] = true;
      this.analyticsService.registerEvent("unfold_filter_attribute", { filter_attribute_name: filterLocal.code }, false);
    }

    this.storageService.set('openFilters', this._filters.map(({ index, code, defaultExpanded }) => ({ index, code, defaultExpanded })));
    this.changeHeight.emit();
    this.changeDetection.detectChanges();
  }

  public clearFilters(): void
  {
    this.valueChanged.emit(null);
    this.hideBar.emit(true);
  }

  public applyFilters(): void
  {
    this.closeFilterBar();
  }

  public changeSingleFilter(filterlocal: Filter): void
  {
    if (filterlocal instanceof Event)
    {
      return;
    }

    this.idToGoBackTo = filterlocal.code;
    this.topPositionOfIdToGoBackTo = this.getFilterPosition(this.idToGoBackTo);

    this.valueChanged.emit(filterlocal);
    this.hideBar.emit(true);
  }

  private createEmptySelectFilter(numberOfValues: number)
  {
    let temp = [];
    for (let i = 0; i < numberOfValues; i++)
    {
      temp.push(null);
    }
    return { values: temp, type: 'Select', name: null, visible: true };
  }

  public trackByIndex(index, item): number
  {
    return item.code;
  }

  public isInterval(type): boolean
  {
    return type === StringVariable.INTERVAL
  }
}
